<script setup>
import { computed, reactive, ref, getCurrentInstance, watch } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Divider from 'primevue/divider';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useCart } from '@/composables/useCart';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

// Use cart composable for delete functionality
const { removeFromCart, updateQuantity: updateCartQuantity } = useCart();

const props = defineProps({
    cartItems: {
        type: Array,
        required: true
    },
    clientCurrency: {
        type: String,
        required: true
    },
    paymentTaxes: {
        type: Object,
        default: () => ({ stripe: 0, pix: 0, boleto: 0, wise: 0 })
    },
    languages: {
        type: Array,
        default: () => []
    },
    canUseDeferredPayment: {
        type: Boolean,
        default: false
    }
});

// Currency formatter
const { formatWithSymbol, formatConvertedPrice, getCurrentCurrency, onCurrencyChange, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const expandedItems = computed(() => {
    if (!props.cartItems || props.cartItems.length === 0) {
        return [];
    }
    
    return props.cartItems.flatMap(item => {
        const entries = [];
        for (let i = 0; i < item.quantity; i++) {
            entries.push({
                ...item,
                linkNumber: i + 1,
                totalForSite: item.quantity
            });
        }
        return entries;
    });
});

const itemForms = ref([]);

watch(expandedItems, (newValue) => {
    if (newValue && newValue.length > 0) {
        itemForms.value = newValue.map(item => ({
            site_id: item.id,
            quantity: 1, 
            type_id: null,
            observations: '',
            selectedPrice: item.price_coin ? item.price : item.converted_price, 
            selectedCoin: item.price_coin || item.converted_coin,
            linkNumber: item.linkNumber,
            totalForSite: item.totalForSite,
            url: item.url,
            link_type: item.link_type,
            types: item.types,
            category: item.category,
            country: item.country,
            language: item.language,
            type_observation: item.type_observation
        }));
    } else {
        itemForms.value = [];
    }
}, { immediate: true });

const defaultTypeId = ref(null);

const availableTypes = computed(() => {
    const typesMap = new Map();

    itemForms.value.forEach(form => {
        form.types.forEach(type => {
            if (!typesMap.has(type.id)) {
                typesMap.set(type.id, {
                    id: type.id,
                    name: type.name
                });
            }
        });
    });

    return Array.from(typesMap.values());
});

const applyDefaultType = () => {
    if (!defaultTypeId.value) return;

    const incompatibleSites = [];
    let appliedCount = 0;

    itemForms.value.forEach((form, index) => {
        const hasType = form.types.some(t => t.id === defaultTypeId.value);

        if (hasType) {
            form.type_id = defaultTypeId.value;
            onTypeChange(index);
            appliedCount++;
        } else {
            if (!incompatibleSites.includes(form.url)) {
                incompatibleSites.push(form.url);
            }
        }
    });

    if (appliedCount > 0) {
        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Default type applied to {{count}} item(s)', { count: appliedCount }),
            life: 3000
        });
    }

    if (incompatibleSites.length > 0) {
        const sitesList = incompatibleSites.join(', ');
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('The following sites do not have this type available: {{sites}}', { sites: sitesList }),
            life: 5000
        });
    }
};

const onTypeChange = (index) => {
    const form = itemForms.value[index];

    if (form.type_id) {
        const selectedType = form.types.find(t => t.id === form.type_id);
        if (selectedType) {
            form.selectedPrice = selectedType.price_coin ? selectedType.price : selectedType.converted_price;
            form.selectedCoin = selectedType.price_coin || selectedType.converted_coin;
        }
    } else {
        const originalItem = props.cartItems.find(item => item.id === form.site_id);
        if (originalItem) {
            form.selectedPrice = originalItem.price_coin ? originalItem.price : originalItem.converted_price;
            form.selectedCoin = originalItem.price_coin || originalItem.converted_coin;
        }
    }
};

const deleting = ref(null);

const deleteItem = async (index) => {
    const form = itemForms.value[index];
    const siteId = form.site_id;

    deleting.value = index;

    try {
        const itemsOfThisSite = itemForms.value.filter(item => item.site_id === siteId);
        const currentQuantity = itemsOfThisSite.length;

        if (currentQuantity > 1) {
            const newQuantity = currentQuantity - 1;

            await updateCartQuantity(siteId, newQuantity);

            itemForms.value.splice(index, 1);

            const remainingItems = itemForms.value.filter(item => item.site_id === siteId);
            remainingItems.forEach((item, idx) => {
                item.linkNumber = idx + 1;
                item.totalForSite = newQuantity;
            });
        } else {
            await removeFromCart(siteId);

            itemForms.value.splice(index, 1);
        }

        window.dispatchEvent(new CustomEvent('cart-updated'));

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Item removed from checkout'),
            life: 3000
        });
    } catch (error) {
        console.error('Delete error:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to remove item'),
            life: 3000
        });
    } finally {
        deleting.value = null;
    }
};

const totalLinks = computed(() => {
    return itemForms.value.reduce((sum, form) => sum + form.quantity, 0);
});

const subtotalPrice = computed(() => {
    return itemForms.value.reduce((sum, form) => {
        const convertedPrice = convertPrice(form.selectedPrice, form.selectedCoin, currentCurrency.value);
        const rounded = Math.ceil(convertedPrice * 20) / 20;
        return sum + (rounded * form.quantity);
    }, 0);
});

const paymentTaxAmount = computed(() => 0);

const totalPrice = computed(() => {
    return subtotalPrice.value + paymentTaxAmount.value;
});

const isFormValid = computed(() => {
    return itemForms.value.every(form => form.type_id !== null);
});

const submitting = ref(false);
const deferredPayment = ref(false);

const submitOrder = async () => {
    if (!isFormValid.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a type for all items'),
            life: 3000
        });
        return;
    }
    
    submitting.value = true;
    
    try {
        // Prepare items for place order page
        const items = itemForms.value.map(item => ({
            site_id: item.site_id,
            quantity: item.quantity,
            type_id: item.type_id,
            observations: item.observations,
        }));
        
        router.post(proxy.route('client.checkout.store'), {
            items,
            selected_currency: currentCurrency.value,
            deferred_payment: deferredPayment.value,
        }, {
            onError: (errors) => {
                console.error('Checkout errors:', errors);
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors.message || $t('Failed to process checkout. Please try again.'),
                    life: 5000
                });
                submitting.value = false;
            }
        });
    } catch (error) {
        console.error('Submit order error:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to process checkout. Please try again.'),
            life: 5000
        });
        submitting.value = false;
    }
};

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';

    const normalizedType = linkType.toUpperCase().trim();

    if (normalizedType.includes('DOFOLLOW')) {
        return 'DOFOLLOW';
    }

    if (normalizedType.includes('SPONSOR')) {
        return 'SPONSORED';
    }

    return 'NOFOLLOW';
};

// Listen for currency changes
onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});
</script>

<template>
    <div class="checkout-page">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-2">
            <div class="lg:col-span-4 mb-6">
                <h1 class="text-2xl sm:text-3xl font-bold text-surface-900 dark:text-surface-0">
                    {{ $t('Checkout') }}
                </h1>
                <p class="text-surface-600">{{ $t('Review your order and complete your purchase') }}</p>
            </div>
            <!-- Cart Items Table -->
            <div class="lg:col-span-3">
                <Card>
                    <template #title>
                        <div class="flex items-center justify-between">
                            <span>{{ $t('Order Items') }}</span>
                            <span class="text-sm font-normal text-surface-600">{{ itemForms?.length || 0 }} {{ (itemForms?.length || 0) === 1 ? $t('link') : $t('links') }}</span>
                        </div>
                    </template>
                    <template #content>
                        <!-- Empty state -->
                        <div v-if="!itemForms || itemForms.length === 0" class="flex flex-col items-center justify-center py-12 text-center">
                            <i class="pi pi-shopping-cart text-6xl text-surface-300 mb-4"></i>
                            <h3 class="text-xl font-semibold text-surface-700 mb-2">{{ $t('Your checkout is empty') }}</h3>
                            <p class="text-surface-500 mb-6">{{ $t('Add items to your cart to proceed') }}</p>
                            <Button
                                :label="$t('Browse Sites')"
                                icon="pi pi-arrow-left"
                                @click="router.visit(proxy.route('client.sites.index'))"
                            />
                        </div>

                        <div v-else-if="itemForms && itemForms.length > 0">
                            <!-- Default Type Selection -->
                            <div class="flex items-center w-[50%] mb-5 mt-3 gap-3">
                                <span class="items-center whitespace-nowrap text-md font-medium text-surface-700">
                                    <i class="pi pi-bolt mr-1"></i>
                                    {{ $t('Select niche of publication') + ':' }}
                                </span>
                                <Select
                                    v-model="defaultTypeId"
                                    :options="availableTypes"
                                    optionLabel="name"
                                    optionValue="id"
                                    @change="applyDefaultType"
                                    :placeholder="$t('Select a niche to apply to all compatible sites')"
                                    class="w-full"
                                />
                            </div>

                            <DataTable
                            :value="itemForms"
                            responsiveLayout="scroll"
                            class="checkout-table"
                            :paginator="false"
                            stripedRows
                        >
                            <!-- URL do Site Column -->
                            <Column :header="$t('Site URL')" style="min-width: 200px">
                                <template #body="{ data }">
                                    <div class="flex items-center gap-2">
                                        <img
                                            :src="`https://www.google.com/s2/favicons?domain=${data.url}`"
                                            class="w-5 h-5"
                                            alt="Site favicon"
                                        />
                                        <div class="flex flex-col">
                                            <span class="text-surface-900">{{ data.url }}</span>
                                            <span v-if="data.totalForSite > 1" class="text-xs text-primary-600">
                                                {{ $t('Link') }} {{ data.linkNumber }}/{{ data.totalForSite }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                            </Column>

                            <!-- Type Column -->
                            <Column :header="$t('Type')" style="min-width: 200px">
                                <template #body="{ data, index }">
                                    <Select
                                        v-model="data.type_id"
                                        :options="data.types"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Choose a type')"
                                        class="w-full"
                                        @change="onTypeChange(index)"
                                    >
                                        <template #option="slotProps">
                                            <div class="flex items-center justify-between w-full">
                                                <span>{{ slotProps.option.name }}</span>
                                                <span class="font-semibold text-primary-600 ml-2">
                                                    {{ formatConvertedPrice(slotProps.option.price || slotProps.option.converted_price, slotProps.option.price_coin || slotProps.option.converted_coin) }}
                                                </span>
                                            </div>
                                        </template>
                                    </Select>
                                    <small v-if="!data.type_id" class="text-red-500">{{ $t('Required') }}</small>
                                </template>
                            </Column>

                            <!-- Link Type Column -->
                            <Column :header="$t('Link Type')" style="min-width: 120px">
                                <template #body="{ data }">
                                    <div class="relative inline-flex items-center justify-center">
                                        <div
                                            class="flex items-center gap-1 px-3 py-2 bg-surface-50 border border-surface-200 rounded-md text-surface-900 text-center"
                                        >
                                            {{ formatLinkType(data.link_type) }}
                                            <i
                                                v-if="data.type_observation"
                                                class="pi pi-info-circle text-blue-500 cursor-pointer"
                                                v-tooltip.top="data.type_observation"
                                                style="font-size: 0.7rem;"
                                            ></i>
                                        </div>
                                    </div>
                                </template>
                            </Column>

                            <!-- Observations Column -->
                            <Column :header="$t('Observations')" style="min-width: 250px">
                                <template #body="{ data }">
                                    <Textarea
                                        v-model="data.observations"
                                        rows="1"
                                        autoResize
                                        class="w-full checkout-textarea"
                                        :placeholder="$t('Add notes...')"
                                    />
                                </template>
                            </Column>

                            <!-- Price Column -->
                            <Column :header="$t('Price')" style="min-width: 100px">
                                <template #body="{ data }">
                                    <span class="font-semibold text-lg text-primary-600 whitespace-nowrap">
                                        {{ formatConvertedPrice(data.selectedPrice, data.selectedCoin) }}
                                    </span>
                                </template>
                            </Column>

                            <!-- Actions Column -->
                            <Column :header="$t('Actions')" style="min-width: 180px">
                                <template #body="{ data, index }">
                                    <div class="flex gap-2 justify-start">
                                        <!-- Delete Button -->
                                        <Button
                                            icon="pi pi-trash"
                                            severity="danger"
                                            outlined
                                            size="small"
                                            @click="deleteItem(index)"
                                            :loading="deleting === index"
                                            :disabled="deleting !== null"
                                        />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                        </div>
                    </template>
                </Card>
            </div>

            <!-- Order Summary -->
            <div class="lg:col-span-1">
                <Card class="sticky top-4">
                    <template #title>
                        {{ $t('Order Summary') }}
                    </template>
                    <template #content>
                        <div class="space-y-4">
                            <div class="flex justify-between items-center">
                                <span class="text-surface-700">{{ $t('Total Links') }}:</span>
                                <span class="font-semibold text-lg">{{ totalLinks }}</span>
                            </div>

                            <Divider />

                            <div class="flex justify-between items-center">
                                <span class="text-surface-700">{{ $t('Subtotal') }}:</span>
                                <span class="font-semibold">
                                    {{ formatWithSymbol(subtotalPrice, currentCurrency) }}
                                </span>
                            </div>

                            <div class="flex justify-between items-center text-sm text-surface-500">
                                <span>{{ $t('Payment Tax') }}:</span>
                                <span>{{ $t('Varies by method') }}</span>
                            </div>

                            <Divider />

                            <div class="flex justify-between items-center">
                                <span class="text-xl font-semibold text-surface-900">{{ $t('Total') }}:</span>
                                <span class="text-2xl font-bold text-primary-600">
                                    {{ formatWithSymbol(totalPrice, currentCurrency) }}
                                </span>
                            </div>

                            <div class="text-sm text-surface-500">
                                {{ $t('Currency') }}: {{ currentCurrency }}
                            </div>

                            <div v-if="canUseDeferredPayment" class="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div class="flex items-start gap-3">
                                    <Checkbox
                                        v-model="deferredPayment"
                                        inputId="deferredPayment"
                                        :binary="true"
                                    />
                                    <label for="deferredPayment" class="cursor-pointer flex-1">
                                        <div class="font-medium text-surface-900">{{ $t('Deferred Payment') }}</div>
                                        <div class="text-sm text-surface-600">{{ $t('When this option is selected, payment will be processed at the end of the purchase.') }}</div>
                                    </label>
                                </div>
                            </div>

                            <Divider />

                            <Button
                                :label="$t('Continue to Add Content')"
                                icon="pi pi-arrow-right"
                                class="w-full"
                                size="large"
                                @click="submitOrder"
                                :loading="submitting"
                                :disabled="!isFormValid || submitting"
                            />

                            <div v-if="!isFormValid" class="text-sm text-orange-600 text-center">
                                <i class="pi pi-exclamation-triangle mr-1"></i>
                                {{ $t('Please select a type for all items') }}
                            </div>
                            
                            <div v-else class="text-sm text-green-600 text-center">
                                <i class="pi pi-check-circle mr-1"></i>
                                {{ $t('Ready to continue!') }}
                            </div>

                            <Button
                                :label="$t('Back to Cart')"
                                icon="pi pi-arrow-left"
                                severity="secondary"
                                outlined
                                class="w-full"
                                @click="router.visit(proxy.route('client.sites.index'))"
                            />
                        </div>
                    </template>
                </Card>
            </div>
        </div>
    </div>
</template>

<style scoped>
.checkout-page {
    padding: 1.5rem;
    margin: 0 auto;
}

/* DataTable Responsive Styles */
.checkout-table {
    font-size: 0.95rem;
}

.checkout-table :deep(.p-datatable-wrapper) {
    overflow-x: auto;
}

.checkout-table :deep(.p-datatable-thead > tr > th) {
    background-color: var(--surface-50);
    font-weight: 600;
    padding: 1rem 0.75rem;
}

.checkout-table :deep(.p-datatable-tbody > tr > td) {
    padding: 1rem 0.75rem;
    vertical-align: top;
}

.checkout-table :deep(.p-select),
.checkout-table :deep(.p-textarea) {
    width: 100%;
}

/* Make textarea same height as select inputs */
.checkout-textarea :deep(textarea) {
    min-height: 2.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .checkout-page {
        padding: 1rem;
    }

    .checkout-table {
        font-size: 0.875rem;
    }

    .checkout-table :deep(.p-datatable-thead > tr > th),
    .checkout-table :deep(.p-datatable-tbody > tr > td) {
        padding: 0.75rem 0.5rem;
    }
}

/* Extra small screens - stack columns */
@media (max-width: 640px) {
    .checkout-table :deep(.p-datatable-wrapper) {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .checkout-table :deep(table) {
        min-width: 900px;
    }
}
</style>
